import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { TableMenu } from '@/components_deprecated'
import { parseISO } from 'date-fns'
import { isMobile } from 'react-device-detect'
import styled, { css, useTheme } from 'styled-components'
import { format } from '@/utils/date-fns'
import type {
  AbsenceTypeNode,
  TimesheetsDayReport,
  TimesheetsDayReportAbsence,
  TimesheetsDayReportOvertimeEntry,
  TimesheetsDayReportTimeEntry,
  TimesheetsSalaryWorkTime,
} from '../types.graphql'
import { useOldAbsenceHandler, useTimesheetHandlers } from './handler.hooks'
import { OldDateCell } from './table-cells'

const StyledIcon = styled(Icon)`
  cursor: pointer;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`

interface TimesheetRowProps {
  day: TimesheetsDayReport
  isWithinInterval: boolean
  projectActive?: boolean
  selectedAbsenceType?: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  absenceOnlyMode?: boolean
  salaryWorkTime: TimesheetsSalaryWorkTime

  onClick: () => void
  onSelectAbsenceType?: (
    absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  ) => void
}

interface RowProps {
  isActive: boolean
  isAbsence: boolean
}

const Row = styled.tr<RowProps>`
  ${props =>
    !props.isActive &&
    css`
      background-color: ${props.theme.colors.gray11};
      color: ${props.theme.colors.gray6} !important;
    `};

  ${props =>
    props.isActive &&
    props.isAbsence &&
    css`
      background-color: ${props.theme.colors.red12};

      &:hover {
        colors: ${props.theme.colors.red};
      }
    `}

  padding: 12px 1rem;

  td {
    box-sizing: content-box;
    height: 30px;
  }

  td.absence-info {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 12px 1rem;
    height: 56px;

    span {
      color: ${props => props.theme.colors.gray5};
      font-weight: 600;
    }
  }
`

export const TimesheetTableRow: React.FC<TimesheetRowProps> = ({
  day,
  projectActive = false,
  salaryWorkTime,
  isWithinInterval,

  onClick = () => {},
}) => {
  const { timesheetHandler } = useTimesheetHandlers(salaryWorkTime)

  const showMenu = day.timeEntries.length + day.absences.length === 1

  const salaryCode =
    day.timeEntries.length === 1 &&
    day.timeEntries.length + day.overtimeEntries.length === 1
      ? day.timeEntries[0]?.salaryCodeOvertime?.name
      : day.timeEntries.length === 1
      ? day.timeEntries[0]?.salaryCodeOrdinary?.name
      : ''

  const menuItems = timesheetHandler.createTimesheetDayReportMenuItems(day)

  const dateColumn = (
    <td key={'date'}>{format(new Date(day.day), 'dd. MMM')}</td>
  )

  const canViewComment = (report: TimesheetsDayReport) =>
    !(
      report.comments === '' ||
      report.comments === 'Ingen kommentar' ||
      report.comments === 'No comment'
    )

  const theme = useTheme()

  return (
    <Row
      key={new Date(day.day).getDate()}
      role="pen"
      isActive={isWithinInterval}
      isAbsence={false}
      onClick={onClick}
    >
      {dateColumn}

      {day.dayStart ? (
        <OldDateCell
          date={parseISO(day.dayStart)}
          side="start"
          type="timeentry"
        />
      ) : (
        <td></td>
      )}

      {day.dayEnd ? (
        <OldDateCell date={parseISO(day.dayEnd)} side="end" type="timeentry" />
      ) : (
        <td></td>
      )}

      <td>{day.totalOrdinary}</td>

      <td>{day.totalPause}</td>

      <td>{day.totalOvertime}</td>

      {!isMobile && (
        <>
          <td>{salaryCode}</td>
          <td>{day?.department?.name}</td>
          {projectActive && <td>{day?.project?.name}</td>}
        </>
      )}

      <td width="130px">
        {canViewComment(day) && (
          <IconContainer>
            <StyledIcon
              color={theme.colors.gray1}
              hoverColor={theme.colors.secondary}
              icon="comment-alt-lines"
              size="16px"
              onClick={() => timesheetHandler.viewComment(day.comments)}
            />
          </IconContainer>
        )}
      </td>

      {showMenu ? <TableMenu items={menuItems} /> : <td></td>}
    </Row>
  )
}

interface TimesheetAbsenceRowProps {
  absence: TimesheetsDayReportAbsence
  date: Date
  isWithinInterval: boolean
  projectActive?: boolean
  salaryWorkTime: TimesheetsSalaryWorkTime

  onClick: () => void
}

export const TimesheetTableAbsenceRow: React.FC<TimesheetAbsenceRowProps> = ({
  absence,
  date,
  isWithinInterval,
  projectActive = false,
  salaryWorkTime,

  onClick = () => {},
}) => {
  const dateColumn = <td key={'date'}>{format(date, 'dd. MMM')}</td>

  const absenceHandler = useOldAbsenceHandler()
  const { timesheetHandler } = useTimesheetHandlers(salaryWorkTime)

  const menuItems = absenceHandler.old_createAbsenceMenuItems(absence, false)

  const theme = useTheme()

  return (
    <Row
      key={date.getDate()}
      role="pen"
      isActive={isWithinInterval}
      isAbsence={true}
      onClick={onClick}
    >
      {dateColumn}
      {absence.datetimeStart ? (
        <OldDateCell
          date={parseISO(absence.datetimeStart)}
          side="start"
          type="absence"
        />
      ) : (
        <td></td>
      )}
      {absence.datetimeEnd ? (
        <OldDateCell
          date={parseISO(absence.datetimeEnd)}
          side="end"
          type="absence"
        />
      ) : (
        <td></td>
      )}
      <td></td>
      <td></td>
      <td></td>
      {!isMobile && (
        <>
          <td></td>
          <td></td>
          {projectActive && <td></td>}
        </>
      )}

      <td width="130px">
        <IconContainer>
          <StyledIcon
            color={theme.colors.gray1}
            hoverColor={theme.colors.secondary}
            icon="comment-alt-lines"
            size="16px"
            onClick={() =>
              timesheetHandler.viewComment(
                (absence.absenceType?.name ?? 'Unknown') + ': ' + absence.reason
              )
            }
          />
        </IconContainer>
      </td>
      <TableMenu items={menuItems} />
    </Row>
  )
}

const SubRow = styled(Row)`
  background-color: ${props => !props.isAbsence && props.theme.colors.gray11};
`

interface TimesheetSubRowProps {
  timeEntry: TimesheetsDayReportTimeEntry
  isWithinInterval: boolean
  hidden?: boolean
  otherEntries?: TimesheetsDayReportTimeEntry[]
  projectActive?: boolean
  salaryWorkTime: TimesheetsSalaryWorkTime

  onClick: () => void
}

export const TimesheetTableSubRow: React.FC<TimesheetSubRowProps> = ({
  timeEntry,
  isWithinInterval,
  hidden = true,
  otherEntries = [],
  projectActive = false,
  salaryWorkTime,

  onClick = () => {},
}) => {
  const translations = useTranslate({
    manual: 'common.manual',
  })
  const { timeEntryHandler, timesheetHandler } =
    useTimesheetHandlers(salaryWorkTime)
  const theme = useTheme()

  const menuItems = timeEntryHandler.createTimesheetTimeEntryMenuItems(
    timeEntry,
    otherEntries
  )

  return (
    <SubRow
      key={new Date(timeEntry.datetimeStart).getDate()}
      role="pen"
      isActive={isWithinInterval}
      isAbsence={false}
      onClick={onClick}
      hidden={hidden}
    >
      <td key={'date'}>
        <i>{translations.manual}</i>
      </td>
      {timeEntry.datetimeStart ? (
        <OldDateCell
          date={parseISO(timeEntry.datetimeStart)}
          side="start"
          type="timeentry"
        />
      ) : (
        <td></td>
      )}
      {timeEntry.datetimeEnd ? (
        <OldDateCell
          date={parseISO(timeEntry.datetimeEnd)}
          side="end"
          type="timeentry"
        />
      ) : (
        <td></td>
      )}
      <td>{timeEntry.totalOrdinary}</td>
      <td>{timeEntry.pause}</td>
      <td>{timeEntry.overtime}</td>
      <td>{timeEntry.salaryCodeOrdinary?.name}</td>
      <td>{timeEntry?.department?.name}</td>
      {projectActive && <td>{timeEntry?.project?.name}</td>}
      <td width="130px">
        <IconContainer>
          <StyledIcon
            color={theme.colors.gray1}
            hoverColor={theme.colors.secondary}
            icon="comment-alt-lines"
            size="16px"
            onClick={() => timesheetHandler.viewComment(timeEntry.comment)}
          />
        </IconContainer>
      </td>
      <TableMenu items={menuItems} />
    </SubRow>
  )
}

interface TimesheetOvertimeSubRowProps {
  overtimeEntry: TimesheetsDayReportOvertimeEntry
  isWithinInterval: boolean
  hidden?: boolean
  projectActive?: boolean

  onClick: () => void
}

export const TimesheetTableOvertimeSubRow: React.FC<
  TimesheetOvertimeSubRowProps
> = ({
  overtimeEntry,
  isWithinInterval,
  hidden = true,
  projectActive = false,

  onClick = () => {},
}) => {
  return (
    <SubRow
      role="pen"
      isActive={isWithinInterval}
      isAbsence={false}
      onClick={onClick}
      hidden={hidden}
    >
      <td key={'date'}></td>
      {overtimeEntry.startTime ? (
        <OldDateCell
          date={parseISO(overtimeEntry.startTime)}
          side="start"
          type="timeentry"
        />
      ) : (
        <td></td>
      )}
      {overtimeEntry.endTime ? (
        <OldDateCell
          date={parseISO(overtimeEntry.endTime)}
          side="end"
          type="timeentry"
        />
      ) : (
        <td></td>
      )}
      <td></td>
      <td></td>
      <td>{overtimeEntry.overtime}</td>
      <td>{overtimeEntry.salaryCode?.name}</td>
      <td>{overtimeEntry?.department?.name}</td>
      {projectActive && <td>{overtimeEntry?.project?.name}</td>}
      <td width="130px"></td>
      <td></td>
    </SubRow>
  )
}

interface TimesheetAbsenceSubRowProps {
  absence: TimesheetsDayReportAbsence
  isWithinInterval: boolean
  hidden?: boolean
  projectActive?: boolean
  salaryWorkTime: TimesheetsSalaryWorkTime

  onClick: () => void
}

export const TimesheetTableAbsenceSubRow: React.FC<
  TimesheetAbsenceSubRowProps
> = ({
  absence,
  isWithinInterval,
  hidden = true,
  projectActive = false,
  salaryWorkTime,

  onClick = () => {},
}) => {
  const translations = useTranslate({
    manual: 'common.manual',
  })

  const theme = useTheme()

  const { timesheetHandler } = useTimesheetHandlers(salaryWorkTime)
  const absenceHandler = useOldAbsenceHandler()

  const menuItems = absenceHandler.old_createAbsenceMenuItems(absence, false)

  return (
    <SubRow
      key={new Date(absence.datetimeStart).getDate()}
      role="pen"
      isActive={isWithinInterval}
      isAbsence={true}
      onClick={onClick}
      hidden={hidden}
    >
      <td key={'date'}>
        <i>{translations.manual}</i>
      </td>
      {absence.datetimeStart ? (
        <OldDateCell
          date={parseISO(absence.datetimeStart)}
          side="start"
          type="absence"
        />
      ) : (
        <td></td>
      )}
      {absence.datetimeEnd ? (
        <OldDateCell
          date={parseISO(absence.datetimeEnd)}
          side="end"
          type="absence"
        />
      ) : (
        <td></td>
      )}
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
      {projectActive && <td></td>}
      <td width="130px">
        <IconContainer>
          <StyledIcon
            color={theme.colors.gray1}
            hoverColor={theme.colors.secondary}
            icon="comment-alt-lines"
            size="16px"
            onClick={() =>
              timesheetHandler.viewComment(
                (absence.absenceType?.name ?? 'Unknown') + ': ' + absence.reason
              )
            }
          />
        </IconContainer>
      </td>
      <TableMenu items={menuItems} />
    </SubRow>
  )
}
